import type * as React from "react"

import type { Column } from "@tanstack/react-table"

import { cn } from "../../cn"
import { Skeleton } from "../Skeleton/Skeleton"
import {
  EDGE_GUTTER_CLASS,
  edgeDividerClasses,
  ESTIMATED_ROW_HEIGHT,
  FLUSH_EDGE_CELLS_CLASS,
  gutterCoverClasses,
  pinnedCellClasses,
  pinnedCellStyle,
  SKELETON_ROW_COUNT,
  STICKY_CELL_DIVIDER,
  type PinMeta,
} from "./TableInternals"

/**
 * A row holding one cell that spans every column, for table states that are
 * not data: the empty state, and the loading and fetching-more notices. A row
 * may only own cells, so the content sits in the cell rather than on the row.
 */
export function FullWidthRow({
  columnCount,
  ariaRowIndex,
  className,
  cellClassName,
  children,
}: {
  columnCount: number
  ariaRowIndex?: number
  className?: string
  cellClassName?: string
  children: React.ReactNode
}) {
  return (
    <div role="row" aria-rowindex={ariaRowIndex} className={className}>
      <div role="cell" aria-colspan={columnCount} className={cellClassName}>
        {children}
      </div>
    </div>
  )
}

export function LoadingRows<T>({
  displayColumns,
  pinnedById,
  gridTemplateColumns,
  rowWidthClass,
  selectable,
  selectSticky,
  selectDivider,
  scrolledX,
  canScrollRight,
  hasActions,
  edgeGutterPx,
  cardRows,
  columnCount,
  ariaRowIndex,
}: {
  displayColumns: Column<T>[]
  pinnedById: Map<string, PinMeta>
  gridTemplateColumns: string
  rowWidthClass: string
  selectable: boolean | undefined
  selectSticky: boolean
  selectDivider: boolean
  scrolledX: boolean
  canScrollRight: boolean
  hasActions: boolean
  edgeGutterPx: number
  cardRows: boolean
  columnCount: number
  ariaRowIndex: number
}) {
  return (
    <>
      {/* The skeleton rows are hidden from assistive tech, so this row is what
          a screen reader finds in the table while it loads. */}
      <FullWidthRow
        columnCount={columnCount}
        ariaRowIndex={ariaRowIndex}
        className="sr-only"
      >
        <span role="status">Loading</span>
      </FullWidthRow>
      {Array.from({ length: SKELETON_ROW_COUNT }).map((_, rowIndex) => (
        <div
          key={rowIndex}
          role="row"
          aria-hidden="true"
          // Fixed row height matches the loaded rows (the virtualizer renders
          // them at `ESTIMATED_ROW_HEIGHT`), so the skeleton doesn't reflow to a
          // shorter row height when the data arrives.
          className={cn(
            "grid items-center",
            // Match the loaded rows: one platter, or divider rows.
            cardRows
              ? cn(
                  "border-x border-b border-coval-border bg-surface-default",
                  rowIndex === 0 && "rounded-t-md border-t",
                  rowIndex === SKELETON_ROW_COUNT - 1 && "rounded-b-md",
                )
              : "border-b border-coval-border",
            EDGE_GUTTER_CLASS,
            !cardRows && FLUSH_EDGE_CELLS_CLASS,
            rowWidthClass,
          )}
          style={{ gridTemplateColumns, height: ESTIMATED_ROW_HEIGHT }}
        >
          {selectable && (
            <div
              style={
                selectSticky
                  ? { position: "sticky", left: edgeGutterPx, zIndex: 1 }
                  : undefined
              }
              className={cn(
                "flex items-center justify-center py-2 pl-3 pr-[var(--table-edge-gutter,12px)]",
                // Match the data rows: carry the row divider under the opaque
                // sticky cell (see STICKY_CELL_DIVIDER).
                selectSticky && cn(STICKY_CELL_DIVIDER, "bg-surface-default"),
                // Mark the selection column's edge while the data columns
                // scroll under it.
                selectDivider && edgeDividerClasses("left"),
                selectSticky && gutterCoverClasses("left"),
              )}
            />
          )}
          {/* Mirror the data rows' pinned layout so pinned columns and the
              selection column stay put while the skeleton scrolls horizontally. */}
          {displayColumns.map((leaf) => {
            const pin = pinnedById.get(leaf.id)
            return (
              <div
                key={leaf.id}
                style={pinnedCellStyle(pin)}
                className={cn(
                  "flex min-w-0 items-center px-3 py-2",
                  pin && pinnedCellClasses(pin, { scrolledX, canScrollRight }),
                )}
              >
                <Skeleton variant="text" />
              </div>
            )
          })}
          {hasActions && (
            <div className="px-3 py-2" style={{ gridColumnEnd: -1 }} />
          )}
        </div>
      ))}
    </>
  )
}
